import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Loader2, Briefcase } from 'lucide-react';

// Short 2-letter badges derived dynamically from name

export default function ServiceLineWorkspaces() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [serviceLines, setServiceLines] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(token, '/service-lines');
      const lines = res.data.serviceLines || [];
      setServiceLines(lines);

      const summaryPromises = lines.map(sl =>
        api.get(token, `/service-lines/${sl.id}/summary`).then(r => r.data).catch(() => null)
      );
      const results = await Promise.all(summaryPromises);
      const map = {};
      results.forEach((r, i) => { if (r) map[lines[i].id] = r; });
      setSummaries(map);
    } catch (err) {
      console.error('Service lines fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-6" data-testid="service-line-workspaces">
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" /></div>
      </div>
    );
  }

  const initials = (id, name) => {
    const override = { 'tax': 'TX', 'tax-advisory': 'TA', 'family-office': 'FO', 'trustee-legal': 'TL', 'wealth': 'WM', 'philanthropy': 'PH' };
    if (override[id]) return override[id];
    const parts = name.split(/[\s/]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div data-testid="service-line-workspaces">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#4A3B32]" />
          <h3 className="font-heading text-base text-[#2C2420]">Service Lines</h3>
        </div>
        <span className="text-xs text-[#8C8078]">Active workload by team</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {serviceLines.map(sl => {
          const summary = summaries[sl.id] || {};
          return (
            <button
              key={sl.id}
              type="button"
              className="text-left rounded-xl border border-[#E6E0D9] bg-white p-4 hover:border-[#A86A3A]/40 hover:shadow-sm transition-all cursor-pointer"
              onClick={() => navigate(`/app/service-lines/${sl.id}`)}
              data-testid={`service-line-${sl.id}`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold mb-3"
                style={{ backgroundColor: sl.color }}
              >
                {initials(sl.id, sl.name)}
              </div>
              <h4 className="font-medium text-sm text-[#2C2420] mb-2 leading-tight">{sl.name}</h4>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#8C8078]">Active Tasks</span>
                  <span className="text-xs font-medium text-[#2C2420]">{summary.activeTasks || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#8C8078]">Members</span>
                  <span className="text-xs font-medium text-[#2C2420]">{summary.memberCount || 0}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
